import type { SiteContent, SiteLocale } from "@/content/schema";
import { ar } from "@/content/locales/ar";
import { en } from "@/content/locales/en";

export const LOCALES: readonly SiteLocale[] = ["en", "ar"];

export function getContent(locale: SiteLocale): SiteContent {
  return locale === "ar" ? ar : en;
}

/** Home path for a locale (`/en` or `/ar`). */
export function localePath(locale: SiteLocale): string {
  return `/${locale}`;
}

/** Locale-prefixed href for a path segment (`services` → `/en/services` or `/ar/services`). */
export function localeHref(locale: SiteLocale, path = ""): string {
  const clean = path.replace(/^\/+/, "").replace(/\/+$/, "");
  if (!clean) return localePath(locale);
  return `/${locale}/${clean}`;
}

/**
 * Per-service deep-link on the Services subpage (ADR-020).
 * Hash fragment (not `?service=`) so the card `id` is the scroll target.
 * EN: `/en/services#digital` · AR: `/ar/services#digital`.
 */
export function serviceHref(locale: SiteLocale, serviceId: string): string {
  return `${localeHref(locale, "services")}#${encodeURIComponent(serviceId)}`;
}

/**
 * Homepage section deep-link from subpages (ADR-020).
 * Used for Products / Story / Contact (and Start a Project).
 * Off-home Work/Services nav uses `localeHref` to real pages instead.
 */
export function homeSectionHref(locale: SiteLocale, section: string): string {
  const base = localePath(locale);
  if (!section || section === "hero") return base;
  return `${base}?section=${encodeURIComponent(section)}`;
}

/** Pair EN ↔ AR URLs for the same subpath (used by SEO alternates). */
export function localeAlternates(path = "") {
  return {
    en: localeHref("en", path),
    ar: localeHref("ar", path),
    "x-default": localeHref("en", path),
  };
}
